// ═══════════════════════════════════════════════════════════════
// Impact — Quick Capture (Mobile-First Assessment Entry)
// Ported from Firebase QuickCapture.jsx → OpenGov Ignatius backend
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, TextInput, Textarea, Select, Button,
  Group, Stack, SegmentedControl, Center, ThemeIcon,
  Divider, Switch,
} from '@mantine/core';
import { IconCheck, IconCamera, IconSparkles, IconBackhoe } from '@tabler/icons-react';
import { useImpactStore } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import PhotoCapture from './components/PhotoCapture';
import LocationInput, { type LocationValue } from './components/LocationInput';
import DamageCategorySelector from './components/DamageCategorySelector';
import DamageTypeSelector from './components/DamageTypeSelector';
import SeverityRating from './components/SeverityRating';
import PACategorySelector from './components/PACategorySelector';
import { toast } from './lib/toast';
import { WORKER_URL } from '../../lib/api-switch';
import { reverseGeocode, getCountyFromCoords } from './lib/gis';

// ── Types ──

type AssessmentMode = 'ia' | 'pa';

interface FormState {
  severity: number | null;
  damageCategory: string;
  paCategory: string;
  damageTypes: string[];
  description: string;
  location: LocationValue;
  photos: File[];
  facilityName: string;
  residenceType: string;
  ownership: string;
  dwellingType: string;
}

const INITIAL_FORM: FormState = {
  severity: null,
  damageCategory: '',
  paCategory: '',
  damageTypes: [],
  description: '',
  location: {},
  photos: [],
  facilityName: '',
  residenceType: '',
  ownership: '',
  dwellingType: '',
};

// ── Helpers ──

function resizeImageToBase64(file: File, maxDim = 1024): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      resolve(dataUrl.split(',')[1]);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

const WORKER = WORKER_URL;

// ── Component ──

export default function QuickCapture() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('impact');
  const { createAssessment, uploadPhoto } = useImpactStore();
  const { createRequest } = useDebrisOpsStore();

  const [assessmentMode, setAssessmentMode] = useState<AssessmentMode>('ia');
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tagForDebris, setTagForDebris] = useState(false);
  const [, setLastAssessmentId] = useState<number | null>(null);

  const update = useCallback((patch: Partial<FormState>) => {
    setForm(prev => ({ ...prev, ...patch }));
  }, []);

  // ── AI Photo Analysis ──

  const handleAiDescribe = async () => {
    if (form.photos.length === 0) {
      toast.warning('Add photos first to generate an AI description.');
      return;
    }
    setAiLoading(true);
    try {
      const images = await Promise.all(
        form.photos.slice(0, 5).map(async (file: File) => ({
          type: 'image' as const,
          source: {
            type: 'base64' as const,
            media_type: 'image/jpeg' as const,
            data: await resizeImageToBase64(file, 1024),
          },
        }))
      );

      const photoCount = images.length;
      const res = await fetch(`${WORKER}/api/claude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Origin': 'https://apps.opengov.com' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 800,
          messages: [{
            role: 'user',
            content: [
              ...images,
              {
                type: 'text',
                text: `I have uploaded ${photoCount} photo${photoCount > 1 ? 's' : ''} from a ${assessmentMode === 'ia' ? 'residential' : 'infrastructure'} damage assessment. Describe the damage visible across ALL ${photoCount} photo${photoCount > 1 ? 's' : ''}, noting what each one shows if they capture different angles or areas. Be concise but thorough (${photoCount > 1 ? '3-5' : '2-3'} sentences). Focus on: type of damage, severity, and structural impact.`,
              },
            ],
          }],
        }),
      });

      const result = await res.json();
      if (result.content?.[0]?.text) {
        update({ description: result.content[0].text });
        toast.success('AI description generated from photos.');
      } else {
        toast.error('AI could not generate a description.');
      }
    } catch (err) {
      console.error('AI describe error:', err);
      toast.error('Failed to generate AI description.');
    } finally {
      setAiLoading(false);
    }
  };

  // ── Submit ──

  const handleSubmit = async () => {
    if (!activeIncident) {
      toast.error('No active incident selected.');
      return;
    }
    if (!form.damageCategory && assessmentMode === 'ia') {
      toast.warning('Please select a damage category.');
      return;
    }
    if (!form.location.lat && !form.location.address) {
      toast.warning('Please provide a location (GPS or address).');
      return;
    }

    setSubmitting(true);
    try {
      const assessmentId = await createAssessment({
        incidentId: String(activeIncident.id),
        assessmentType: assessmentMode === 'ia' ? 'individual' : 'public',
        damageCategory: form.damageCategory,
        paCategory: form.paCategory,
        severity: form.severity,
        address: form.location.address || '',
        county: form.location.county || '',
        fipsCode: form.location.fipsCode || '',
        stateCode: form.location.state || '',
        latitude: form.location.lat || null,
        longitude: form.location.lng || null,
        damageTypes: form.damageTypes,
        description: form.description || '',
        method: 'mobile',
        assessorName: user?.fullName || '',
        assessorEmail: user?.email || '',
        facilityName: assessmentMode === 'pa' ? form.facilityName : '',
        residenceType: form.residenceType,
        ownership: form.ownership,
        dwellingType: form.dwellingType,
      });

      // Upload photos
      if (form.photos.length > 0) {
        for (let i = 0; i < form.photos.length; i++) {
          try {
            const base64 = await resizeImageToBase64(form.photos[i], 1024);
            await uploadPhoto(assessmentId, base64, i);
          } catch (err) {
            console.error(`Photo upload ${i} failed:`, err);
          }
        }
      }

      // Tag for debris removal if checked
      if (tagForDebris) {
        try {
          await createRequest({
            incidentId: String(activeIncident.id),
            assessmentId: String(assessmentId),
            address: form.location.address || '',
            latitude: form.location.lat ? String(form.location.lat) : '',
            longitude: form.location.lng ? String(form.location.lng) : '',
            damageCategory: form.damageCategory || '',
            damageTypes: JSON.stringify(form.damageTypes || []),
            description: form.description || '',
            status: 'pending',
            taggedBy: user?.email ?? '',
            taggedAt: new Date().toISOString(),
          });
        } catch { /* non-blocking */ }
      }

      setLastAssessmentId(assessmentId);
      setSuccess(true);
      toast.success(tagForDebris ? 'Assessment saved & tagged for debris removal!' : 'Assessment saved successfully!');
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save assessment.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── No Incident Guard ──

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <Text size="lg" fw={600} c="dimmed">No Active Incident</Text>
          <Text size="sm" c="dimmed">Select an incident from the sidebar to begin assessments.</Text>
          <Button variant="light" onClick={() => navigate('/incidents')}>
            Go to Incidents
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!canEdit) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <Text size="lg" fw={600} c="dimmed">View Only</Text>
          <Text size="sm" c="dimmed">You do not have permission to create assessments.</Text>
          <Button variant="light" onClick={() => navigate('/impact/assessments')}>
            View Assessments
          </Button>
        </Stack>
      </Center>
    );
  }

  // ── Success Overlay ──

  if (success) {
    return (
      <Center py={80}>
        <Stack align="center" gap="md">
          <ThemeIcon size={80} radius="xl" color="green" variant="filled">
            <IconCheck size={48} />
          </ThemeIcon>
          <Title order={3}>Assessment Saved!</Title>
          <Group gap="sm">
            <Button variant="light" onClick={() => navigate('/impact/assessments')}>
              View All Assessments
            </Button>
            <Button onClick={() => {
              setForm(INITIAL_FORM);
              setTagForDebris(false);
              setSuccess(false);
              setLastAssessmentId(null);
            }}>
              New Assessment
            </Button>
          </Group>
        </Stack>
      </Center>
    );
  }

  // ── Render ──

  return (
    <div>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">Quick Capture</Title>
          <Text size="xs" c="dimmed">
            {activeIncident.name} — {assessmentMode === 'ia' ? 'Individual' : 'Public'} Assessment
          </Text>
        </div>
        <Button variant="subtle" size="xs" onClick={() => navigate('/impact')}>
          Back to Dashboard
        </Button>
      </Group>

      {/* Assessment Mode Toggle */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Text fw={500} size="sm" mb="xs">Assessment Type</Text>
        <SegmentedControl
          value={assessmentMode}
          onChange={(v) => {
            setAssessmentMode(v as AssessmentMode);
            update({ damageCategory: '', paCategory: '', damageTypes: [], facilityName: '' });
          }}
          data={[
            { value: 'ia', label: 'Individual (IA)' },
            { value: 'pa', label: 'Public (PA)' },
          ]}
          fullWidth
          size="md"
        />
      </Paper>

      {/* Photos */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Group justify="space-between" mb="xs">
          <Group gap="xs">
            <IconCamera size={18} />
            <Text fw={500} size="sm">Photos</Text>
          </Group>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconSparkles size={14} />}
            onClick={handleAiDescribe}
            loading={aiLoading}
            disabled={form.photos.length === 0}
          >
            AI Describe
          </Button>
        </Group>
        <PhotoCapture
          photos={form.photos}
          onChange={(photos) => update({ photos })}
          onGpsExtracted={(gps) => {
            if (!form.location.lat) {
              update({ location: { ...form.location, lat: gps.lat, lng: gps.lng } });
              toast.success('Location extracted from photo EXIF data.');
              // Reverse geocode to fill address, county, FIPS
              Promise.all([
                reverseGeocode(gps.lat, gps.lng),
                getCountyFromCoords(gps.lat, gps.lng),
              ]).then(([address, county]) => {
                setForm(prev => ({
                  ...prev,
                  location: {
                    ...prev.location,
                    lat: gps.lat,
                    lng: gps.lng,
                    address: address?.address || prev.location.address || '',
                    street: address?.street || prev.location.street || '',
                    city: address?.city || prev.location.city || '',
                    state: county?.state || address?.state || prev.location.state || '',
                    county: county?.county || address?.county || prev.location.county || '',
                    fipsCode: county?.fipsCode || prev.location.fipsCode || '',
                    zip: address?.zip || prev.location.zip || '',
                  },
                }));
              }).catch(() => {});
            }
          }}
        />
      </Paper>

      {/* Location */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <LocationInput
          value={form.location}
          onChange={(loc) => update({ location: loc })}
        />
      </Paper>

      {/* Damage Assessment */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Stack gap="md">
          {assessmentMode === 'ia' ? (
            <>
              <DamageCategorySelector
                value={form.damageCategory}
                onChange={(val) => update({ damageCategory: val })}
              />
              <DamageTypeSelector
                value={form.damageTypes}
                onChange={(val) => update({ damageTypes: val })}
                mode="ia"
              />
              <Divider />

              <Select
                label="Residence Type"
                placeholder="Select type"
                data={[
                  { value: 'single-family', label: 'Single Family' },
                  { value: 'multi-family', label: 'Multi-Family' },
                  { value: 'apartment', label: 'Apartment' },
                  { value: 'mobile-home', label: 'Mobile Home' },
                  { value: 'condo', label: 'Condo/Townhouse' },
                  { value: 'other', label: 'Other' },
                ]}
                value={form.residenceType || null}
                onChange={(v) => update({ residenceType: v || '' })}
                clearable
              />
              <Select
                label="Ownership"
                placeholder="Select"
                data={[
                  { value: 'owner', label: 'Owner Occupied' },
                  { value: 'renter', label: 'Renter Occupied' },
                  { value: 'vacant', label: 'Vacant' },
                  { value: 'unknown', label: 'Unknown' },
                ]}
                value={form.ownership || null}
                onChange={(v) => update({ ownership: v || '' })}
                clearable
              />
            </>
          ) : (
            <>
              <TextInput
                label="Facility Name"
                placeholder="e.g., Main St Bridge, City Hall"
                value={form.facilityName}
                onChange={(e) => update({ facilityName: e.currentTarget.value })}
              />
              <PACategorySelector
                value={form.paCategory}
                onChange={(val) => update({ paCategory: val || '' })}
              />
              <DamageTypeSelector
                value={form.damageTypes}
                onChange={(val) => update({ damageTypes: val })}
                mode="pa"
              />
              <SeverityRating
                value={form.severity}
                onChange={(val) => update({ severity: val })}
              />
            </>
          )}
        </Stack>
      </Paper>

      {/* Description */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Textarea
          label="Description"
          placeholder="Describe the damage observed..."
          value={form.description}
          onChange={(e) => update({ description: e.currentTarget.value })}
          minRows={3}
          autosize
        />
      </Paper>

      {/* Submit */}
      <Group justify="space-between" mb="xl" align="center">
        <Switch
          label="Tag for Debris Removal"
          checked={tagForDebris}
          onChange={e => setTagForDebris(e.currentTarget.checked)}
          color="orange"
          thumbIcon={tagForDebris ? <IconBackhoe size={12} /> : undefined}
        />
        <Group gap="sm">
          <Button
            variant="subtle"
            onClick={() => {
              setForm(INITIAL_FORM);
              setTagForDebris(false);
              toast.info('Form cleared.');
            }}
          >
            Clear
          </Button>
          <Button
            size="lg"
            onClick={handleSubmit}
            loading={submitting}
            leftSection={<IconCheck size={18} />}
          >
            Save Assessment
          </Button>
        </Group>
      </Group>
    </div>
  );
}
